import { cellRenderer, customHeader } from "../SAR/SARGrid";
import DataGrid, { Column, DataGridRef } from "devextreme-react/data-grid";
import type { LTIPPlan } from "@/types/types";
import { GridSkeleton } from "@/components/Skeletons";
import { formatNumber } from "devextreme/localization";
import { useRef } from "react";
import { adjustSortArrowPosition, checkClientsBirthdates, getOwnerName } from "@/helper/helper";
import useHeaderChange from "@/hooks/useHeaderChange";
import { useLTIP } from "@/hooks/useLTIP";
import { LTIPType } from "@/types/enums";
import { useParams } from "react-router";
import HouseholdClientTabs from "@/components/HouseholdClientTabs";
import NoDataMessage from "@/components/NoDataMessage";
import useSelectedClient from "@/hooks/useSelectedClient";
import useDefaultSelectedClient from "@/hooks/useDefaultSelectedClient";
import Heading from "@/components/Heading";
import { NoContactBirthDateMessage } from "../HouseholdContact";

export default function CompensationDeferralPlan() {
  const dxGridRef = useRef<DataGridRef>(null);
  const { id } = useParams();
  const { household, upLoading } = useHeaderChange(id!, "Compensation Deferral Plan");
  const { selectedClient, setSelectedClient } = useSelectedClient(household, LTIPType.CDP);

  const {
    data: cdpData,
    isLoading: cdpIsLoading,
    isError: cdpIsError,
    isRefetching: cdpIsRefetching,
  } = useLTIP(id!, LTIPType.CDP, selectedClient, household);

  useDefaultSelectedClient(household, cdpData, selectedClient, setSelectedClient, LTIPType.CDP);

  const fa = cdpData?.data[0];
  const ds = fa?.planYears;

  if (checkClientsBirthdates(household)) {
    return <NoContactBirthDateMessage household={household} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {upLoading ? (
        <div className="max-w-[16rem]">
          <GridSkeleton columns={2} rows={1} />
        </div>
      ) : household?.secondaryContact && cdpData?.bothClientsHaveData ? (
        <HouseholdClientTabs
          household={household}
          selectedClientExternalCode={selectedClient}
          setSelectedClientExternalCode={setSelectedClient}
        />
      ) : null}
      {cdpIsLoading || cdpIsRefetching ? <CDPSkeleton /> : null}
      {!ds?.length && !cdpIsLoading && !cdpIsRefetching ? <NoDataMessage /> : null}
      {cdpIsError ? <div>Failed fetching Compensation Deferral Plan</div> : null}
      {ds?.length && !cdpIsLoading && !cdpIsRefetching ? (
        <>
          {!cdpData?.bothClientsHaveData && !household?.isSingle ? (
            <Heading
              text={`Compensation Deferral Plan for ${getOwnerName(household, selectedClient)}`}
            />
          ) : null}
          <DataGrid
            dataSource={ds ?? []}
            className="ao-grid"
            showBorders={true}
            showRowLines={true}
            showColumnLines={true}
            keyExpr={"externalCode"}
            ref={dxGridRef}
            wordWrapEnabled={true}
            onCellPrepared={(e) => {
              if (e.rowType === "header" || e.column.dataField === "planYear") {
                e.cellElement.style.backgroundColor = "var(--primary-color)";
                e.cellElement.style.color = "white";
                e.cellElement.style.fontWeight = "500";
              }
              adjustSortArrowPosition(dxGridRef);
            }}
          >
            <Column
              dataField="planYear"
              dataType="date"
              allowSorting={false}
              sortOrder={"asc"}
              allowEditing={false}
              width={160}
              alignment={"center"}
              headerCellRender={() => customHeader(`plan-year`, "plan year")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.year ? cellRenderer(cellData, "plan-year", item?.year) : "";
              }}
            />
            <Column
              dataField="accountType"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`account-type`, "account type")}
              cellRender={(cellData) =>
                fa?.type ? cellRenderer(cellData, "account-type", fa?.type, "!text-base") : ""
              }
            />
            <Column
              dataField="baseSalaryDirectDeferral"
              dataType="number"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`base-salary-direct-deferral`, "base salary", "direct deferral")
              }
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.baseSalaryDirectDeferral
                  ? cellRenderer(
                      cellData,
                      "base-salary-direct-deferral",
                      formatNumber(item?.baseSalaryDirectDeferral / 100, "#%")
                    )
                  : "";
              }}
            />
            <Column
              dataField="baseSalaryExcessDeferral"
              dataType="number"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`base-salary-excess-deferral`, "base salary", "excess deferral")
              }
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.baseSalaryExcessDeferral
                  ? cellRenderer(
                      cellData,
                      "base-salary-excess-deferral",
                      formatNumber(item?.baseSalaryExcessDeferral / 100, "#%")
                    )
                  : "";
              }}
            />
            <Column
              dataField="annualIncentivePlanDeferral"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`annual-incentive-plan-deferral`, `annual incentive`, "plan deferral")
              }
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.annualIncentivePlanDeferral
                  ? cellRenderer(
                      cellData,
                      "annual-incentive-plan-deferral",
                      formatNumber(item?.annualIncentivePlanDeferral / 100, "#%")
                    )
                  : "";
              }}
            />
            <Column
              dataField="distributionElection"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() =>
                customHeader(`distribution-election`, "distribution election")
              }
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.distributionElection
                  ? cellRenderer(cellData, "distribution-election", item?.distributionElection)
                  : "";
              }}
            />
            <Column
              dataField="firstPaymentDate"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`first-payment-date`, "first payment date")}
              cellRender={(cellData) => {
                const item: LTIPPlan = cellData.data;

                return item?.firstPaymentDate
                  ? cellRenderer(cellData, "first-payment-date", item?.firstPaymentDate)
                  : "";
              }}
            />
            <Column
              dataField="oneTimeChangesUsed"
              dataType="string"
              allowSorting={false}
              allowEditing={false}
              minWidth={130}
              alignment={"center"}
              headerCellRender={() => customHeader(`one-time-changes-used`, "changes used")}
              cellRender={(cellData) =>
                fa?.changesUsed
                  ? cellRenderer(cellData, "one-time-changes-used", fa?.changesUsed)
                  : ""
              }
            />
          </DataGrid>
        </>
      ) : null}
    </div>
  );
}

export const CDPSkeleton = () => {
  return <GridSkeleton columns={8} rows={4} />;
};
